/**
 * The reporting window, carried over from the Creators tab in oga-reporting and
 * from the tracker, so all three sites read alike.
 *
 * The one decision worth restating: a window ends at the newest snapshot, not at
 * "now". NUMERO's data is imported roughly daily with gaps, so anchoring to the
 * clock would make every figure appear to sag between imports for no reason
 * other than today's not having landed yet.
 */

export type PeriodKey = "7D" | "30D" | "MTD" | "YTD" | "LIFETIME" | "CUSTOM";

export const PERIOD_KEYS: PeriodKey[] = ["7D", "30D", "MTD", "YTD", "LIFETIME", "CUSTOM"];

export const PERIOD_LABELS: Record<PeriodKey, string> = {
  "7D": "Week",
  "30D": "Month",
  MTD: "MTD",
  YTD: "YTD",
  LIFETIME: "Lifetime",
  CUSTOM: "Custom",
};

export function isPeriodKey(value: unknown): value is PeriodKey {
  return typeof value === "string" && (PERIOD_KEYS as string[]).includes(value);
}

const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` in local time, which is how the calendar addresses days. */
export function toISODate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function fromISODate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function addDays(iso: string, days: number): string {
  const date = fromISODate(iso);
  if (!date) return iso;
  return toISODate(new Date(date.getTime() + days * DAY_MS));
}

export function daysBetween(from: string, to: string): number {
  const a = fromISODate(from);
  const b = fromISODate(to);
  if (!a || !b) return 0;
  return Math.round((b.getTime() - a.getTime()) / DAY_MS);
}

export type Range = { from: string; to: string };

/**
 * Turn a period key into the `from`/`to` the reporting API wants.
 *
 * `latest` is the newest snapshot date, not today. Relative periods count back
 * from it for the reason above, and this mirrors what the far end does with a
 * bare `period=`, which means a CUSTOM range built here and a relative period
 * sent as-is resolve to the same window.
 */
export function resolveRange(
  period: PeriodKey,
  bounds: { earliest: string; latest: string },
  custom?: { from?: string | null; to?: string | null }
): Range {
  const { earliest, latest } = bounds;

  if (period === "CUSTOM") {
    const from = custom?.from && fromISODate(custom.from) ? custom.from : earliest;
    const to = custom?.to && fromISODate(custom.to) ? custom.to : latest;
    return from <= to ? { from, to } : { from: to, to: from };
  }

  if (period === "LIFETIME") return { from: earliest, to: latest };
  if (period === "7D") return { from: clamp(addDays(latest, -7), earliest, latest), to: latest };
  if (period === "30D") return { from: clamp(addDays(latest, -30), earliest, latest), to: latest };

  const end = fromISODate(latest) ?? new Date();
  const start = new Date(end);
  if (period === "YTD") start.setMonth(0);
  start.setDate(1);
  return { from: clamp(toISODate(start), earliest, latest), to: latest };
}

function clamp(value: string, low: string, high: string): string {
  if (value < low) return low;
  if (value > high) return high;
  return value;
}

/**
 * The equal length window immediately before, which is what every delta badge
 * compares against. Matching the reference dashboards rather than inventing a
 * new baseline keeps the three readable side by side.
 */
export function priorRange(range: Range, bounds: { earliest: string }): Range | null {
  const span = daysBetween(range.from, range.to);
  if (span <= 0) return null;
  const to = addDays(range.from, -1);
  const from = addDays(to, -span);
  // A prior window that reaches back further than the data goes is not a
  // comparison, and presenting it as one is how a page misleads.
  if (from < bounds.earliest) return null;
  return { from, to };
}

const DAY_MONTH: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };

export function describeRange(period: PeriodKey, range: Range | null): string {
  if (period !== "CUSTOM") return PERIOD_LABELS[period];
  if (!range) return "Custom";
  const format = (iso: string) => (fromISODate(iso) ?? new Date()).toLocaleDateString("en-AU", DAY_MONTH);
  const from = format(range.from);
  const to = format(range.to);
  return from === to ? from : `${from} to ${to}`;
}

export function describeDay(iso: string): string {
  return (fromISODate(iso) ?? new Date()).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}
